import React, { useState } from 'react';
import { ChatSession } from '../../services/genericChatService';
import { GenericChatService } from '../../services/genericChatService';
import { AICharacter } from '../../services/aiCharacterService';
import './ChatSessionList.css';

interface ChatSessionListProps {
  sessions: ChatSession[];
  currentSessionId: number | null;
  loadingSessions: boolean;
  onSessionSelect: (sessionId: number) => void;
  onSessionDeleted: (sessionId: number) => void;
  onSessionsUpdated: () => void;
  characters?: AICharacter[];
}

const ChatSessionList: React.FC<ChatSessionListProps> = ({
  sessions,
  currentSessionId,
  loadingSessions,
  onSessionSelect,
  onSessionDeleted,
  onSessionsUpdated,
  characters = []
}) => {
  const [renamingSessionId, setRenamingSessionId] = useState<number | null>(null);
  const [renameInput, setRenameInput] = useState('');
  const [deletingSessionId, setDeletingSessionId] = useState<number | null>(null);

  const formatVendorModel = (session: ChatSession): string | null => {
    // If character_id exists, look up character name
    if (session.character_id) {
      const character = characters.find(c => c.id === session.character_id);
      if (character) {
        return `Character: ${character.name}`;
      }
      return 'Character: Unknown';
    }
    
    // If provider and model exist, display them
    if (session.provider && session.model) {
      const providerName = session.provider.charAt(0).toUpperCase() + session.provider.slice(1);
      return `${providerName}: ${session.model}`;
    }
    
    // Fallback: show nothing
    return null;
  };

  const handleRenameSession = async (sessionId: number, newTitle: string) => {
    if (!newTitle.trim()) return;
    
    try {
      await GenericChatService.updateSessionTitle(sessionId, newTitle.trim());
      setRenamingSessionId(null);
      setRenameInput('');
      onSessionsUpdated();
    } catch (error) {
      console.error('Error renaming session:', error);
    }
  };

  const handleDeleteSession = async (sessionId: number) => {
    try {
      await GenericChatService.deleteSession(sessionId);
      setDeletingSessionId(null);
      onSessionDeleted(sessionId);
      onSessionsUpdated();
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const startRename = (session: ChatSession) => {
    setRenamingSessionId(session.id);
    setRenameInput(session.title || '');
  };

  const cancelRename = () => {
    setRenamingSessionId(null);
    setRenameInput('');
  };

  if (loadingSessions) {
    return (
      <div className="chat-session-list-loading">Loading...</div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="chat-session-list-empty">No chat history</div>
    );
  }

  // Sort sessions by last_user_message_at (descending), fallback to created_at
  const sortedSessions = [...sessions].sort((a, b) => {
    const aTime = a.last_user_message_at ? new Date(a.last_user_message_at).getTime() : new Date(a.created_at).getTime();
    const bTime = b.last_user_message_at ? new Date(b.last_user_message_at).getTime() : new Date(b.created_at).getTime();
    return bTime - aTime; // Descending order
  });

  return (
    <div className="chat-session-list">
      {sortedSessions.map((session) => {
        // Use last_user_message_at if available, otherwise fallback to created_at
        const displayTime = session.last_user_message_at || session.created_at;
        const displayDate = new Date(displayTime);
        const vendorModel = formatVendorModel(session);
        
        return (
        <div 
          key={session.id} 
          className={`chat-session-item ${currentSessionId === session.id ? 'active' : ''}`}
        >
          {renamingSessionId === session.id ? (
            <div className="chat-session-rename">
              <input
                type="text"
                value={renameInput}
                onChange={(e) => setRenameInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleRenameSession(session.id, renameInput);
                  } else if (e.key === 'Escape') {
                    cancelRename();
                  }
                }}
                onBlur={() => {
                  if (renameInput.trim()) {
                    handleRenameSession(session.id, renameInput);
                  } else {
                    cancelRename();
                  }
                }}
                className="chat-session-rename-input"
                autoFocus
              />
            </div>
          ) : (
            <>
              <div 
                className="chat-session-item-content"
                onClick={() => onSessionSelect(session.id)}
              >
                <div className="chat-session-item-title">
                  {session.title || 'Untitled Chat'}
                </div>
                {session.first_message_preview && (
                  <div className="chat-session-item-preview">
                    {session.first_message_preview}
                  </div>
                )}
                {vendorModel && (
                  <div className="chat-session-item-vendor-model">
                    {vendorModel}
                  </div>
                )}
                <div className="chat-session-item-time">
                  {displayDate.toLocaleDateString()} {displayDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </div>
              </div>
              <div className="chat-session-item-actions">
                <button
                  className="chat-session-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    startRename(session);
                  }}
                  title="Edit session title"
                >
                  ✏️
                </button>
                {deletingSessionId === session.id ? (
                  <div className="chat-session-delete-confirm">
                    <button
                      className="chat-session-delete-confirm-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSession(session.id);
                      }}
                      title="Confirm delete"
                    >
                      ✓
                    </button>
                    <button
                      className="chat-session-delete-cancel-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingSessionId(null);
                      }}
                      title="Cancel delete"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    className="chat-session-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletingSessionId(session.id);
                    }}
                    title="Delete session"
                  >
                    🗑️
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      );
      })}
    </div>
  );
};

export default ChatSessionList;

